import React from "react";
import Avatar from "../common/Avatar";
import ClubCrest from "../common/ClubCrest";
import {
  classifyInboxChannel,
  counterpartType,
} from "../../hooks/inbox/useInbox";

// InboxSearchResults — the two-section search surface (design 3b / 4b). While the
// inbox search carries a query it REPLACES the per-tab channel list with:
//   • CONTACTS — channels whose counterpart NAME matches the query, queried across
//     ALL tabs by member name (not tab-scoped). The 4b scope chip filters this
//     group by counterpart type; matches outside the chosen scope drop into a
//     dimmed "IN OTHER TYPES" group that stays tappable to widen the scope.
//   • MESSAGES — message-body hits (client.search) across the viewer's channels,
//     each tagged with the tab it lives in, click-to-open.
//
// This is the root fix for the reported 3b defect: the old inbox filter ANDed a
// channel-NAME autocomplete with the active tab, so typing a counterpart's name
// returned "No DM chats yet" even when that counterpart had a thread. Counterpart
// search must key on member name, cross-tab — which is exactly what this does.

// Counterpart-type → tag pill + accent, matching the 3b/4b frames.
const TYPE_META = {
  agents: { label: "Agent", bg: "#F0F6FF", fg: "#1466D8", border: "#D6E6FF" },
  clubs: { label: "Club", bg: "#FDF1E7", fg: "#B4550A", border: "#F3DDC4" },
  players: { label: "Player", bg: "#EAF6EE", fg: "#17803D", border: "#CFEAD8" },
  staff: { label: "Staff", bg: "#F2F4F7", fg: "#475467", border: "#E4E9F0" },
  other: { label: "", bg: "#F2F4F7", fg: "#475467", border: "#E4E9F0" },
};

// Canonical lane → the location tag shown on a MESSAGES hit (design 3b rows).
const TAB_TAG = {
  dm: "DM",
  last_minute: "Last Minute",
  agent_requests: "Agent Req.",
  club_requests: "Club Req.",
  trusted_agent: "Trusted Agent",
  scout_requests: "Scout Req.",
};

const SCOPE_LABEL = {
  clubs: "CLUBS",
  agents: "AGENTS",
  staff: "STAFF",
  players: "PLAYERS",
};

// Pull the counterpart identity out of the channel's stamped `users` array (the
// same source CustomChannelPreview reads). Works for both a live queryChannels
// channel (ch.data.users) and a client.search result channel (r.channel.users).
const deriveCounterpart = (users, selfId) => {
  const arr = Array.isArray(users) ? users : [];
  const other = arr.find((u) => String(u?.id) !== String(selfId)) || {};
  return {
    name: other.name || other.email || "Unknown",
    role: other.role || "",
    agencyName: other.agency_name || "",
    clubName: other.club_name || "",
    clubLeague: other.club_league || "",
    clubLogo: other.club_logo || null,
    image: other.profile_picture_url || other.image || null,
  };
};

// Best-effort subtitle for a CONTACTS row: agency for agents, club (· league)
// for clubs, a humanised role otherwise. Honest — never invents "your client".
const contactSubtitle = (type, cp) => {
  if (type === "agents") return cp.agencyName || "";
  if (type === "clubs") {
    if (cp.clubName)
      return cp.clubLeague ? `${cp.clubName} · ${cp.clubLeague}` : cp.clubName;
    return "";
  }
  const raw = String(cp.role || "").trim();
  if (!raw) return "";
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const SectionLabel = ({ children, topBorder = false }) => (
  <div
    style={{
      padding: "10px 14px 4px",
      font: "600 9.5px ui-monospace,Menlo,monospace",
      color: "#98A2B3",
      letterSpacing: ".06em",
      borderTop: topBorder ? "1px solid #F0F2F6" : undefined,
      marginTop: topBorder ? 8 : undefined,
    }}
  >
    {children}
  </div>
);

const TypePill = ({ type }) => {
  const meta = TYPE_META[type] || TYPE_META.other;
  if (!meta.label) return null;
  return (
    <span
      className="flex-shrink-0"
      style={{
        padding: "1px 6px",
        borderRadius: 6,
        background: meta.bg,
        border: `1px solid ${meta.border}`,
        fontSize: "9.5px",
        fontWeight: 600,
        color: meta.fg,
      }}
    >
      {meta.label}
    </span>
  );
};

const CounterpartAvatar = ({ cp, type }) => (
  <div className="relative flex-shrink-0" style={{ width: 36, height: 36 }}>
    <Avatar
      src={cp.image}
      alt={cp.name}
      size={36}
      initialsSize={12.5}
      fallbackText={cp.name}
    />
    {type === "clubs" && (
      <ClubCrest
        name={cp.clubName || cp.name}
        logoUrl={cp.clubLogo}
        variant="badge"
        avatarSize={36}
      />
    )}
  </div>
);

// A CONTACTS row (design 3b CONTACTS / 4b type groups). The whole row opens the
// existing thread; the right-hand "Message" affordance mirrors the frame.
const ContactRow = ({ cp, type, dimmed, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="w-full text-left flex items-center gap-2.5 hover:bg-gray-50 transition-colors"
    style={{ padding: "9px 14px", opacity: dimmed ? 0.55 : 1 }}
  >
    <CounterpartAvatar cp={cp} type={type} />
    <div className="flex-1 min-w-0 flex flex-col gap-px">
      <div className="flex items-center gap-1.5 min-w-0">
        <span
          className="text-[13px] font-semibold text-[#101828] truncate"
          title={cp.name}
        >
          {cp.name}
        </span>
        <TypePill type={type} />
      </div>
      <span className="text-[11px] text-[#98A2B3] truncate">
        {dimmed ? "Shown dimmed — tap to widen scope" : contactSubtitle(type, cp)}
      </span>
    </div>
    {!dimmed && (
      <span
        className="flex-shrink-0"
        style={{
          padding: "5px 12px",
          borderRadius: 8,
          background: "#fff",
          border: "1px solid #BBD7FF",
          color: "#1466D8",
          fontSize: "11.5px",
          fontWeight: 600,
        }}
      >
        Message
      </span>
    )}
  </button>
);

// A MESSAGES hit row (design 3b MESSAGES). Name + location tag + time, then the
// matching message text; opens the channel the hit lives in.
const MessageRow = ({ hit, onOpen }) => {
  const { cp, type, tag, time, text } = hit;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left flex items-center gap-2.5 hover:bg-gray-50 transition-colors"
      style={{ padding: "9px 14px" }}
    >
      <CounterpartAvatar cp={cp} type={type} />
      <div className="flex-1 min-w-0 flex flex-col gap-px">
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className="text-[13px] font-semibold text-[#101828] truncate"
            title={cp.name}
          >
            {cp.name}
          </span>
          <span
            className="flex-shrink-0"
            style={{
              padding: "1px 6px",
              borderRadius: 6,
              background: "#F8FAFC",
              border: "1px solid #E6EAF0",
              fontSize: "9.5px",
              fontWeight: 500,
              color: "#667085",
            }}
          >
            {tag}
          </span>
          {time && (
            <span className="ml-auto flex-shrink-0 text-[10.5px] text-[#98A2B3]">
              {time}
            </span>
          )}
        </div>
        <span className="text-[12px] text-[#667085] truncate" title={text}>
          {text}
        </span>
      </div>
    </button>
  );
};

const InboxSearchResults = ({
  chatClient,
  userId,
  query,
  searchScope = "all",
  onWidenScope,
  onOpenChannel,
}) => {
  const [contacts, setContacts] = React.useState([]);
  const [messageHits, setMessageHits] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!chatClient || !userId || !query) {
      setContacts([]);
      setMessageHits([]);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const timer = setTimeout(async () => {
      const q = query.trim();
      const lower = q.toLowerCase();

      // CONTACTS — channels whose counterpart NAME matches, cross-tab. Primary
      // query keys on member name (the fix for the name-vs-channel-name defect);
      // if the app config rejects the member.user.name filter, fall back to the
      // channel-name autocomplete so the group still returns.
      const queryContacts = async () => {
        const base = { type: "messaging", members: { $in: [userId] } };
        try {
          return await chatClient.queryChannels(
            { ...base, "member.user.name": { $autocomplete: q } },
            [{ last_message_at: -1 }],
            { limit: 30, state: true, watch: false },
          );
        } catch (e) {
          try {
            return await chatClient.queryChannels(
              { ...base, name: { $autocomplete: q } },
              [{ last_message_at: -1 }],
              { limit: 30, state: true, watch: false },
            );
          } catch (e2) {
            return [];
          }
        }
      };

      // MESSAGES — body hits across the viewer's channels.
      const queryMessages = async () => {
        try {
          const res = await chatClient.search(
            { type: "messaging", members: { $in: [userId] } },
            q,
            { limit: 20 },
            [{ created_at: -1 }],
          );
          return res?.results || [];
        } catch (e) {
          return [];
        }
      };

      const [chans, hits] = await Promise.all([
        queryContacts(),
        queryMessages(),
      ]);
      if (cancelled) return;

      // Build CONTACTS: keep only channels whose counterpart actually matches the
      // query text (guards against the member.user.name filter also matching the
      // viewer's own name), then attach type + counterpart.
      const seen = new Set();
      const built = [];
      for (const ch of chans) {
        const users = ch?.data?.users;
        const cp = deriveCounterpart(users, userId);
        const hay = [cp.name, cp.agencyName, cp.clubName]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(lower)) continue;
        const cid = ch?.cid || ch?.id;
        if (!cid || seen.has(cid)) continue;
        seen.add(cid);
        built.push({
          cid,
          type: counterpartType(ch, userId),
          cp,
        });
      }
      setContacts(built);

      // Build MESSAGES: derive counterpart + tab tag off each hit's channel.
      const builtHits = [];
      const seenMsg = new Set();
      for (const r of hits) {
        const msg = r?.message;
        const chan = r?.message?.channel || r?.channel;
        if (!msg || !chan) continue;
        const key = msg.id || `${chan.cid}:${msg.created_at}`;
        if (seenMsg.has(key)) continue;
        seenMsg.add(key);
        const cp = deriveCounterpart(chan.users, userId);
        const tab = classifyInboxChannel({ id: chan.id, data: chan });
        const created = msg.created_at ? new Date(msg.created_at) : null;
        const time = created
          ? created.toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit",
              hour12: false,
            })
          : "";
        builtHits.push({
          cid: chan.cid || chan.id,
          cp,
          type: counterpartType({ data: chan }, userId),
          tag: chan.archived ? "Archive" : TAB_TAG[tab] || "DM",
          time,
          text: (msg.text || "").replace(/\n/g, " "),
        });
      }
      setMessageHits(builtHits);
      setLoading(false);
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [chatClient, userId, query]);

  // Partition CONTACTS by the active scope chip (4b). scope "all" → one CONTACTS
  // group; a specific scope → the in-scope group (labelled by type) plus a dimmed
  // "IN OTHER TYPES" group for the rest.
  const scoping = searchScope && searchScope !== "all";
  const inScope = scoping
    ? contacts.filter((c) => c.type === searchScope)
    : contacts;
  const otherScope = scoping
    ? contacts.filter((c) => c.type !== searchScope)
    : [];
  const inScopeLabel = scoping ? SCOPE_LABEL[searchScope] || "CONTACTS" : "CONTACTS";

  const nothing =
    !loading && contacts.length === 0 && messageHits.length === 0;

  return (
    <div className="pr-1">
      {inScope.length > 0 && (
        <>
          <SectionLabel>
            {inScopeLabel} · {inScope.length}
          </SectionLabel>
          {inScope.map((c) => (
            <ContactRow
              key={c.cid}
              cp={c.cp}
              type={c.type}
              onOpen={() => onOpenChannel?.(c.cid)}
            />
          ))}
        </>
      )}

      {otherScope.length > 0 && (
        <>
          <SectionLabel topBorder>IN OTHER TYPES · {otherScope.length}</SectionLabel>
          {otherScope.map((c) => (
            <ContactRow
              key={c.cid}
              cp={c.cp}
              type={c.type}
              dimmed
              onOpen={() => onWidenScope?.()}
            />
          ))}
        </>
      )}

      {messageHits.length > 0 && (
        <>
          <SectionLabel topBorder={contacts.length > 0}>
            MESSAGES · {messageHits.length}
          </SectionLabel>
          {messageHits.map((h, i) => (
            <MessageRow
              key={`${h.cid}-${i}`}
              hit={h}
              onOpen={() => onOpenChannel?.(h.cid)}
            />
          ))}
        </>
      )}

      {loading && contacts.length === 0 && messageHits.length === 0 && (
        <p className="px-4 py-6 text-center text-[12px] text-[#98A2B3]">
          Searching…
        </p>
      )}

      {nothing && (
        <div className="flex flex-col items-center justify-center text-center px-6 py-12 gap-1.5">
          <p className="text-[13.5px] font-semibold text-[#344054]">
            No matches
          </p>
          <p className="text-[11.5px] text-[#98A2B3] leading-relaxed max-w-[240px]">
            No contacts or messages match “{query}”. Try a different name or word.
          </p>
        </div>
      )}
    </div>
  );
};

export default InboxSearchResults;
